import React, {
  useState,
  useContext,
  createContext,
  ReactElement,
  useEffect,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react';
import { setLocalItem, getLocalItem } from '../common';
import JoinProfileDetails from '../components/join/JoinProfileDetails';
import JoinCreateAccountV2 from '../components/join/JoinCreateAccountV2';
import JoinAboutMeAndIdealDate from '../components/join/JoinAboutMeAndIdealDate';
import JoinProfileInfo from '../components/join/JoinProfileInfo';
import { EVENT_NAMES } from '../constants/mixpanel';
import { ACCOUNT_TYPE_ATTRACTIVE_USER, INTERESTS_OPTIONS } from '../components/join/constants';
import JoinBulkPhotoUpload from '../components/join/JoinBulkPhotoUpload';
import { PhotoListTypes } from '../modules/gallery/constants';
import JoinOpeningOffer from '../components/join/JoinOpeningOffer';
import store from '../store';
import JoinOfferTutorial from '../components/join/JoinOfferTutorial';
import { IAttributesState } from '../reducers/types/Attributes';
import {
  ALL_MANDATORY_2PHOTOS,
  ALL_MANDATORY_3PHOTOS,
} from '../utils/evidentlyFeatures/onboardingFieldsMandatory/common';
import JoinPhotoCustomDescription from '../components/join/bulkUpload/JoinPhotoCustomDescription';
import JoinProfileProfession from '../components/join/JoinProfileProfession';
import { mixpanelTrackEvent } from '../utils/mixpanel/common';
import { isEmpty } from 'lodash';
import SelfieVerificationCheck from '../components/join/selfieVerification/SelfieVerificationCheck';

interface IUseJoin {
  listOfSteps: any[];
  currentStepIndex: number;
  currentStep: any;
  currentFormData: any;
  stepsFormData: any;
  showSkipmodal: boolean;
  skipModalCallback: any;
  isEuUser: boolean;
  onBoardingMandatoryVariant: string | null;
  uploadedPhoto?: PhotoListTypes | [];
  attributes: IAttributesState;
  isSubmitFailed: boolean;
  prevStep: () => any;
  nextStep: (isSkip?: boolean) => any;
  convertToFormData: (hasPreData?: boolean) => any;
  setCurrentFormData: (formData: any) => any;
  onFormSubmitted: (
    formData: any,
    isPrivate: boolean,
    trackMixpanelCBAction: (hashId: string, isAccountIsMixpanelEligableEnabled?: boolean) => void,
    previewPhotos?: PhotoListTypes,
    isUpdate?: boolean,
    setUploadedPhoto?: (uploadedPhoto: PhotoListTypes) => any
  ) => any;
  setCurrentStepIndex: Dispatch<SetStateAction<string | number>>;
  setShowJoinForm: Dispatch<SetStateAction<boolean>>;
  setShowSkipModal: Dispatch<SetStateAction<boolean>>;
  handleShowSkipModal: (callback?: any) => any;
  setSkipModalCallback: Dispatch<SetStateAction<any>>;
  selectedInterestsLabel: () => Array<string>;
  updateOfferTutorialState: () => void;
  setUploadedPhoto?: (uploadedPhoto: PhotoListTypes) => any;
  skippedStepCounter: Record<string, boolean>;
  setSkippedStepCounter: Dispatch<SetStateAction<any>>;
  isMandatoryField: (step?: string) => boolean;
  setSVStatus: Dispatch<SetStateAction<any>>;
}

const JoinContext = createContext({});
const JoinProvider = (props: any): ReactElement => {
  const localItem = JSON.parse(getLocalItem('join_onboarding_predata'));
  const { onFormSubmitted, setShowJoinForm, isEuUser, updateOfferTutorialState, attributes, liveness } = props;
  const [currentStepIndex, setCurrentStepIndex] = useState(localItem?.currentStepIndex ?? 0);
  const [skippedStepCounter, setSkippedStepCounter] = useState<Record<string, boolean>>({});
  const [svStatus, setSVStatus] = useState<any>(null);

  const onboardingVariants = [ALL_MANDATORY_2PHOTOS, ALL_MANDATORY_3PHOTOS];

  const ONBOARDING_STEPS = [
    {
      id: 'create_account',
      title: 'Awesome!',
      description: (
        <p style={{ width: 311, textAlign: 'center', margin: '8px 0' }}>{`Let’s get the basics out of the way`}</p>
      ),
      component: <JoinCreateAccountV2 />,
      formData: {
        sex: null,
        account_type: null,
        username: '',
        email: '',
        month: null,
        day: null,
        year: null,
        interested_in: null,
        looking_male: false,
        looking_female: false,
        agreements: true,
        ref: '',
        referral: '',
        address: '',
        placeId: '',
        isEuUser: store.getState().attributes.isEuUser,
        errors: null,
        'h-captcha-response': '',
      },
    },
    {
      id: 'profile_details',
      title: 'Great!',
      description: <p style={{ margin: '8px 0' }}>Now we need some details about you</p>,
      component: <JoinProfileDetails />,
      formData: {
        body_type: null,
        dating_just_dating: false,
        dating_friendship: false,
        dating_marriage: false,
        dating_married: false,
        dating_adult: false,
        dating_mentorship: false,
        height: null,
        heightValue: [],
        heightIsFt: false,
      },
    },
    {
      id: 'am_join_opening_offer',
      title: 'Looking good!',
      description: (
        <p style={{ textAlign: 'center', margin: '8px 0' }}>
          Now for the fun stuff. What would you like to get paid to go on a date?
        </p>
      ),
      component: <JoinOpeningOffer />,
      formData: {
        default_offer_amount: null,
      },
    },
    {
      id: 'profile_info',
      title: 'Great!',
      description: <p style={{ margin: '8px 0' }}>Now we need some details about you</p>,
      component: <JoinProfileInfo />,
      formData: {
        children: null,
        smoking: null,
        drinking: null,
      },
    },
    {
      id: 'profile_profession',
      title: 'Great!',
      description: <p style={{ margin: '8px 0' }}>Now we need some details about you</p>,
      component: <JoinProfileProfession />,
      formData: {
        education: null,
        occupation: null,
        income: null,
        net_worth: null,
      },
    },
    {
      id: 'about_me_and_ideal_date',
      title: 'Perfect!',
      description: (
        <p style={{ textAlign: 'center', margin: '8px 0' }}>
          Just a few more questions to let everyone know what you’re looking for
        </p>
      ),
      component: <JoinAboutMeAndIdealDate />,
      formData: {
        description: '',
        seeking: '',
      },
    },
    {
      id: 'bulk_photo_upload',
      title: 'Show off a little!',
      description: <JoinPhotoCustomDescription />,
      component: <JoinBulkPhotoUpload />,
      formData: {
        photos: [],
      },
    },
    {
      id: 'selfie_verification',
      title: null,
      description: null,
      component: <SelfieVerificationCheck isJoinFlow={true} />,
      formData: {
        askPermission: '',
      },
    },
    {
      id: 'join_offer_tutorial',
      title: 'One more thing!',
      description: <></>,
      component: <JoinOfferTutorial />,
      formData: {
        offer_description: '',
      },
    },
  ];

  const [stepsFormData, setStepFormData] = useState(
    localItem?.stepsFormData ??
      ONBOARDING_STEPS.map(({ formData }) => {
        return {
          ...formData,
        };
      })
  );

  const [showSkipmodal, setShowSkipModal] = useState(false);
  const [skipModalCallback, setSkipModalCallback] = useState(null);
  const [uploadedPhoto, setUploadedPhoto] = useState<PhotoListTypes>([]);

  const onBoardingMandatoryVariant =
    stepsFormData[0]?.account_type === 1 ? ALL_MANDATORY_2PHOTOS : ALL_MANDATORY_3PHOTOS;

  const filteredSteps = useMemo(() => {
    const shouldSkipSelfieVerification = !isEmpty(liveness) && !liveness.required;
    const isSelfiVerificationRequired =
      !isEmpty(liveness) && liveness.required && ['required', 'not_started'].includes(liveness.status);
    const shouldFilterSelfieVerification =
      isEmpty(svStatus) &&
      shouldSkipSelfieVerification &&
      !isSelfiVerificationRequired &&
      ONBOARDING_STEPS[currentStepIndex]?.id !== 'selfie_verification';

    const updatedSteps = shouldFilterSelfieVerification
      ? ONBOARDING_STEPS.filter(step => step.id !== 'selfie_verification')
      : ONBOARDING_STEPS;

    // prevent empty onboarding step
    if (currentStepIndex > 7 && shouldFilterSelfieVerification && isEmpty(updatedSteps[currentStepIndex])) {
      setCurrentStepIndex(7);
    }

    const noAmJoinOpeningOfferSteps = updatedSteps.filter(step => step.id !== 'am_join_opening_offer');
    // Joined as GM
    // hide Join Opening Offer step

    if (stepsFormData[0]?.account_type === 1) {
      return noAmJoinOpeningOfferSteps;
    }

    return updatedSteps;
  }, [stepsFormData, liveness]);

  const currentStep = useMemo(() => filteredSteps[currentStepIndex], [currentStepIndex, filteredSteps]);
  const currentFormData = useMemo(() => stepsFormData[currentStepIndex], [currentStepIndex, stepsFormData]);

  const prevStep = () => {
    const formStepsNames = {
      1: 'Profile details step 1',
      2: 'Profile details step 2',
      3: 'Profile details step 3',
      4: 'About Step',
      5: 'Photo Step',
      6: 'Offer Tutorial',
    };

    mixpanelTrackEvent({
      eventName: EVENT_NAMES.JOIN_CLICKED_BACK,
      payload: {
        eventProperties: {
          Step: formStepsNames[currentStepIndex],
        },
      },
    });

    setCurrentStepIndex(prev => (prev > 0 ? prev - 1 : prev));
  };

  const nextStep = (isSkip = false) => {
    if (isSkip) {
      setCurrentFormData(filteredSteps[currentStepIndex].formData);
    }

    setCurrentStepIndex(prev => prev + 1);
  };

  const convertToFormData = (hasPreData = true): FormData => {
    const formDataObj = new FormData();

    stepsFormData.map(item => {
      for (const [key, value] of Object.entries(item)) {
        if (value === null || value === undefined) continue;

        if (key !== 'photos') {
          formDataObj.append(key, value as string);
        }
      }
    });

    const joinPreData = JSON.parse(getLocalItem('join_predata'));

    if (hasPreData && joinPreData && joinPreData !== null) {
      const { password, token, email, site, phoneNumber, emailToken } = joinPreData;

      if (token && typeof token !== 'undefined') {
        formDataObj.set('token', token);
        formDataObj.set('site', site);
      } else if (emailToken && typeof emailToken !== 'undefined') {
        formDataObj.set('email_token', emailToken);
      } else {
        formDataObj.set('password', password);
      }

      formDataObj.set('email', email);

      if (phoneNumber) {
        formDataObj.set('phone_number', phoneNumber);
      }
    }

    return formDataObj;
  };

  const setCurrentFormData = (state: any) => {
    setStepFormData(prev => {
      const updatedObj = { ...prev[currentStepIndex], ...state };

      return [...prev.slice(0, currentStepIndex), updatedObj, ...prev.slice(currentStepIndex + 1)];
    });
  };

  useEffect(() => {
    if (currentStepIndex && ONBOARDING_STEPS[currentStepIndex]?.id === 'profile_details') {
      const { account_type } = stepsFormData[0];
      const isAttractive = account_type === ACCOUNT_TYPE_ATTRACTIVE_USER;
      const hasDefaultOfferAmount = Object.prototype.hasOwnProperty.call(stepsFormData[2], 'default_offer_amount');
      const shouldInsertDefaultOfferAmountFields = isAttractive && !hasDefaultOfferAmount;

      if (shouldInsertDefaultOfferAmountFields) {
        stepsFormData.splice(2, 0, ONBOARDING_STEPS[2].formData);
      } else if (!isAttractive && hasDefaultOfferAmount) {
        stepsFormData.splice(2, 1);
      }
    }

    let lsData = stepsFormData;

    if (currentStepIndex === 0) {
      const [stepOne, ...rest] = stepsFormData;
      const stepOneData = { ...stepOne };

      delete stepOneData['h-captcha-response'];

      lsData = [stepOneData, ...rest];
    }

    setLocalItem(
      'join_onboarding_predata',
      JSON.stringify({
        currentStepIndex,
        stepsFormData: lsData,
      })
    );
  }, [currentStepIndex, stepsFormData]);

  const handleShowSkipModal = (callback?: any) => {
    const localShowOnboardSkip = JSON.parse(getLocalItem('onboarding_skip_modal'));

    if (localShowOnboardSkip) {
      if (typeof callback === 'function') {
        callback();
        return false;
      }

      nextStep(true);
    } else {
      setShowSkipModal(true);

      if (typeof callback === 'function') {
        setSkipModalCallback(() => callback);
      }
    }
  };

  const selectedInterestsLabel = () => {
    return INTERESTS_OPTIONS.reduce((accumulator, currentValue) => {
      const value = currentFormData?.[currentValue.value];

      if (typeof value !== 'undefined' && value) {
        return [...accumulator, currentValue.label];
      } else {
        return accumulator;
      }
    }, [] as Array<string>);
  };

  const isMandatoryField = (): boolean => {
    return onboardingVariants.includes(onBoardingMandatoryVariant);
  };

  const values: IUseJoin = {
    listOfSteps: filteredSteps,
    currentStepIndex,
    currentStep,
    currentFormData,
    stepsFormData,
    onFormSubmitted,
    showSkipmodal,
    skipModalCallback,
    setShowJoinForm,
    isEuUser: isEuUser,
    onBoardingMandatoryVariant,
    updateOfferTutorialState,
    uploadedPhoto,
    attributes,
    isSubmitFailed: props.errors.error,
    prevStep,
    nextStep,
    convertToFormData,
    setCurrentFormData,
    setCurrentStepIndex,
    setShowSkipModal,
    handleShowSkipModal,
    setSkipModalCallback,
    selectedInterestsLabel,
    setUploadedPhoto,
    skippedStepCounter,
    setSkippedStepCounter,
    isMandatoryField,
    setSVStatus,
  };

  return <JoinContext.Provider value={values} {...props} />;
};

const useJoin = (): IUseJoin => useContext(JoinContext) as IUseJoin;

export { JoinProvider, useJoin };
